import { createContext, useContext, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

interface AdminSession {
  id: string;
  token?: string;
  username: string;
  role: string;
}

interface AdminAuthContextType {
  isAdminAuthenticated: boolean;
  adminSession: AdminSession | null;
  adminLogin: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  adminLogout: () => void;
  impersonating: string | null;
  loginAsRestaurant: (restaurantName: string) => void;
  stopImpersonating: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [adminSession, setAdminSession] = useState<AdminSession | null>(() => {
    const stored = sessionStorage.getItem("kf-admin-session");
    return stored ? JSON.parse(stored) : null;
  });
  const [impersonating, setImpersonating] = useState<string | null>(null);

  const isAdminAuthenticated = !!adminSession;

  const adminLogin = async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-login", {
        body: { username, password },
      });

      if (error) return { success: false, error: "An error occurred. Please try again." };
      if (!data?.success) return { success: false, error: data?.error || "Invalid username or password" };

      const session: AdminSession = data.session;

      if (data.authBridge?.token_hash) {
        const { error: otpErr } = await supabase.auth.verifyOtp({
          token_hash: data.authBridge.token_hash,
          type: "magiclink",
        });
        if (otpErr) console.error("admin auth bridge verifyOtp failed", otpErr);
      }

      setAdminSession(session);
      sessionStorage.setItem("kf-admin-session", JSON.stringify(session));
      return { success: true };
    } catch {
      return { success: false, error: "An error occurred. Please try again." };
    }
  };

  const adminLogout = () => {
    const token = adminSession?.token;
    if (token) {
      supabase.functions.invoke("session-logout", { body: { admin_session_id: token } }).catch(() => {});
    }
    supabase.auth.signOut().catch(() => {});
    setAdminSession(null);
    sessionStorage.removeItem("kf-admin-session");
  };

  const loginAsRestaurant = (name: string) => setImpersonating(name);
  const stopImpersonating = () => setImpersonating(null);

  return (
    <AdminAuthContext.Provider value={{ isAdminAuthenticated, adminSession, adminLogin, adminLogout, impersonating, loginAsRestaurant, stopImpersonating }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
};
